import { useCallback, useEffect, useRef, useState } from "react";

import {
  cancelUploadSession,
  pollUploadStatus,
  removePersistedUpload,
  savePersistedUpload,
  statusError,
  uploadFileDirectly,
} from "utils/upload";

const INITIAL_STATE = {
  phase: "idle",
  progress: 0,
  filename: null,
  source: "uploaded",
  startedAt: null,
  session: null,
  status: null,
  error: null,
};

// `scope` identifies which list this hook's uploads belong to (e.g. "home" or
// `project:<id>`) so an in-flight upload can be persisted to localStorage and
// reattached — by this page or a fresh load of it — after a refresh. Pass it
// whenever the page should survive a reload; omit it for uploads that don't
// need to (nothing is persisted, matching the old in-memory-only behavior).
export function useDirectUpload({ scope } = {}) {
  const [state, setState] = useState(INITIAL_STATE);
  const controllerRef = useRef(null);
  const promiseRef = useRef(null);
  const sessionIdRef = useRef(null);
  const modeRef = useRef(null);

  const start = useCallback(
    (file, options = {}) => {
      if (controllerRef.current) return Promise.reject(new Error("An upload is already in progress."));

      const controller = new AbortController();
      controllerRef.current = controller;
      modeRef.current = "start";
      setState({
        ...INITIAL_STATE,
        phase: "creating",
        filename: options.filename || file?.name || "Untitled upload",
        source: options.source || "uploaded",
        startedAt: new Date().toISOString(),
      });

      const operation = uploadFileDirectly(file, {
        ...options,
        signal: controller.signal,
        onSession: (session) => {
          sessionIdRef.current = session?.id ?? null;
          setState((current) => ({ ...current, phase: "uploading", session, status: session.status }));
          if (scope && session?.id) {
            savePersistedUpload({
              id: session.id,
              scope,
              filename: options.filename || file?.name || "Untitled upload",
              source: options.source || "uploaded",
              name: options.name,
              language: options.language,
              project: options.project ?? null,
              startedAt: new Date().toISOString(),
            });
          }
          options.onSession?.(session);
        },
        onProgress: (progress) => {
          setState((current) => ({ ...current, phase: "uploading", progress }));
          options.onProgress?.(progress);
        },
        onStatus: (upload) => {
          setState((current) => ({
            ...current,
            phase: upload.status === "validating" ? "validating" : current.phase,
            status: upload.status,
          }));
          options.onStatus?.(upload);
        },
        onTransferComplete: () => {
          setState((current) => ({ ...current, phase: "validating", progress: 100 }));
          options.onTransferComplete?.();
        },
      })
        .then((upload) => {
          setState((current) => ({ ...current, phase: "completed", status: upload.status }));
          return upload;
        })
        .catch((error) => {
          setState((current) => ({
            ...current,
            phase: controller.signal.aborted ? "cancelled" : "failed",
            error,
          }));
          throw error;
        })
        .finally(() => {
          if (scope && sessionIdRef.current) removePersistedUpload(sessionIdRef.current);
          controllerRef.current = null;
          promiseRef.current = null;
          sessionIdRef.current = null;
          modeRef.current = null;
        });

      promiseRef.current = operation;
      return operation;
    },
    [scope]
  );

  // Reattaches to an upload session that was persisted by a previous
  // instance of this hook (before a refresh, or before this component
  // unmounted). There's no file to re-send — S3 already has the bytes, or
  // never will again — so this only ever polls `GET /uploads/<id>/` for a
  // terminal status, same as the tail end of `start`.
  const resume = useCallback((record) => {
    if (!record?.id) return Promise.reject(new Error("Nothing to resume."));
    if (controllerRef.current) return Promise.reject(new Error("An upload is already in progress."));

    const controller = new AbortController();
    controllerRef.current = controller;
    sessionIdRef.current = record.id;
    modeRef.current = "resume";
    setState({
      ...INITIAL_STATE,
      phase: "validating",
      filename: record.filename || "Untitled upload",
      source: record.source || "uploaded",
      startedAt: record.startedAt || new Date().toISOString(),
      status: "validating",
    });

    const operation = pollUploadStatus(record.id, {
      signal: controller.signal,
      onStatus: (upload) => {
        setState((current) => ({
          ...current,
          phase: upload.status === "validating" ? "validating" : current.phase,
          status: upload.status,
        }));
      },
    })
      .then((upload) => {
        if (upload?.status !== "uploaded") throw statusError(upload);
        setState((current) => ({ ...current, phase: "completed", status: upload.status }));
        return upload;
      })
      .catch((error) => {
        setState((current) => ({
          ...current,
          phase: controller.signal.aborted ? "cancelled" : "failed",
          error,
        }));
        throw error;
      })
      .finally(() => {
        // A resumed poll only ever stops here on a real outcome (success,
        // failure, or an explicit cancel() — see the unmount effect below),
        // so it's always safe to drop the persisted record at this point.
        removePersistedUpload(record.id);
        controllerRef.current = null;
        promiseRef.current = null;
        sessionIdRef.current = null;
        modeRef.current = null;
      });

    promiseRef.current = operation;
    return operation;
  }, []);

  const cancel = useCallback(async () => {
    if (!controllerRef.current) return;
    setState((current) => ({ ...current, phase: "cancelling" }));
    const mode = modeRef.current;
    const idToCancel = sessionIdRef.current;
    controllerRef.current.abort();
    try {
      await promiseRef.current;
    } catch {
      // The upload operation owns the resulting error state.
    }
    // `start`'s own uploadFileDirectly already cancels the session on abort.
    // A resumed poll never sent bytes and doesn't own that cleanup, so it has
    // to ask the server to cancel explicitly.
    if (mode === "resume" && idToCancel) {
      try {
        await cancelUploadSession(idToCancel);
      } catch {
        // Best effort — the session may already be past a cancellable status.
      }
    }
  }, []);

  const reset = useCallback(() => setState(INITIAL_STATE), []);

  useEffect(
    () => () => {
      // A resumed poll doesn't own an S3 transfer to interrupt — leave it
      // running across a mere unmount so the persisted id can still be
      // reattached (by this page remounting, or another tab) instead of
      // being cancelled just because the user briefly navigated away. A
      // fresh `start()` still aborts on unmount, as before.
      if (modeRef.current !== "resume") controllerRef.current?.abort();
    },
    []
  );

  useEffect(() => {
    const uploadInProgress = ["creating", "uploading", "validating", "cancelling"].includes(state.phase);
    if (!uploadInProgress) return undefined;

    const warnBeforeLeaving = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [state.phase]);

  const busy = ["creating", "uploading", "validating", "cancelling"].includes(state.phase);
  const visible = busy;
  return { ...state, busy, visible, start, resume, cancel, reset };
}
